// Detect which ONNX Runtime backend to use: WebGPU when available, else WASM-SIMD.
//
// Gotchas
// -------
// * We ship ONLY the SIMD+threaded ORT-Web wasm
//   (`vendor/ort-wasm-simd-threaded.wasm`). A browser without SIMD will
//   refuse to load it (Safari <16.4 / Chrome <91 — effectively never on
//   our target devices: iPad 9 + iPadOS 17 has SIMD; any Chromebook
//   still receiving updates has SIMD). `src/runtime_checks.js` throws
//   early with a clear message if SIMD is missing.
// * MAX_THREADS = 8 is empirical. More pthreads net out SLOWER for
//   conformer-small (many small ops, high pthread launch overhead).
//   iPad 9 has 6 cores (2P+4E) so it naturally settles at 6; old
//   Chromebooks usually 2–4. The min() handles all cases safely.
// * `simd: true` is now the ORT-Web default but we set it explicitly so
//   the wasm-file resolver always picks the SIMD variant on first load.
// * `proxy: true` runs ORT (and its pthread pool) on a dedicated worker rather
//   than the calling thread, so a ~RTF·windowMs inference no longer blocks the
//   main thread — which on the realtime mic path is also driving React render
//   and the audio worklet's message pump. We only ever create a WASM-EP session
//   (the placement wiring forces Backend.Wasm; WebGPU is never selected here),
//   so there is no GPUBuffer zero-copy to preserve by staying on the main
//   thread; output crosses the worker boundary via transferable (cheap).
//   NOTE: confirm with an on-device long-task profile before relying on it; it
//   is a one-line revert if the proxy worker misbehaves on a target browser.

// String values intentionally match ORT-Web execution provider names AND
// the Backend enum in asr.js. Keep them in sync.
const FALLBACK = "wasm";
const PREFERRED = "webgpu";
const DEFAULT_HW_THREADS = 4;
const MAX_THREADS = 8;

export async function detectBackend() {
  const gpu = navigator.gpu;
  if (!gpu) return FALLBACK;

  try {
    const adapter = await gpu.requestAdapter({ powerPreference: "high-performance" });
    if (!adapter) return FALLBACK;
    return PREFERRED;
  } catch {
    // Intentional swallow: requestAdapter() throws on browsers where
    // navigator.gpu exists but the adapter pipeline is broken (Firefox
    // with WebGPU enabled but no Vulkan loader; headless Chromium without
    // --enable-unsafe-webgpu; Linux with an unsupported driver). All
    // recoverable by falling back to WASM. runtime_checks.webGpu still
    // reports the raw capability accurately, so diagnostics don't lie.
    return FALLBACK;
  }
}

// ORT log policy (audited):
//   * "Unknown CPU vendor" — fires once in headless Chromium when ORT cannot read
//     CPUID through the wasm sandbox. Purely informational; no behavior change.
//   * "Some nodes were not assigned to the preferred execution providers" — fires
//     for WebGPU sessions because ORT intentionally pins shape-related ops to CPU
//     for performance (its own docs say so). Not a defect.
// Both are vendor-internal at "warning" level; production users universally raise
// the threshold to "error". We do not suppress real errors.
export function configureOrt(ort, vendorBaseUrl) {
  if (typeof vendorBaseUrl !== "string" || vendorBaseUrl.length === 0) {
    throw new TypeError("configureOrt: vendorBaseUrl must be a non-empty string");
  }
  ort.env.wasm.wasmPaths = vendorBaseUrl;
  ort.env.wasm.numThreads = Math.min(navigator.hardwareConcurrency || DEFAULT_HW_THREADS, MAX_THREADS);
  ort.env.wasm.simd = true;
  ort.env.wasm.proxy = true;
  ort.env.logLevel = "error";
}
